import dynostore, { dynamicReducers } from "@redux-dynostore/core";
import { combineReducers, createStore, compose } from "redux";
import { composeWithDevTools } from "redux-devtools-extension";
import { applyMiddleware } from "redux-subspace";
import thunk from "redux-thunk";
import adminRegisterReducer from "./modules/adminRegister";
import cookieConsent from "./modules/cookieConsent";
import { refreshSource, source } from "./modules/http";
import layout from "./modules/layout";
import loginReducer from "./modules/login";
import profileManagement from "./modules/profileManagement";
import recoveryReducer from "./modules/recovery";
import themeReducer from "./modules/theme";
import axios from "axios";

const axiosInstance = axios.create();

export default function configureStore(preloadedState) {
  const appReducer = combineReducers({
    loginReducer,
    adminRegisterReducer,
    layout,
    recoveryReducer,
    themeReducer,
    cookieConsent,
    profileManagement,
  });

  const rootReducer = (state, action) => {
    return appReducer(
      action.type === "LOGOUT" ? resetReduxAxios() : state,
      action
    );
  };

  const resetReduxAxios = () => {
    source.cancel();

    refreshSource();
    return undefined;
  };

  const middlewares = applyMiddleware(thunk);

  const getEnhancers = () => {
    // Check if we're not in production environment
    // NODE_ENV is automatically set to "production" when running `npm run build`
    // In development (e.g., `npm start`), it's set to "development"
    if (process?.env?.NODE_ENV !== "production") {
      // For non-production environments:
      // 1. Enable Redux DevTools for easier debugging
      // 2. Apply middleware (likely including Redux Thunk)
      // 3. Add dynostore with dynamic reducers for runtime reducer management
      return composeWithDevTools(middlewares, dynostore(dynamicReducers()));
    }

    // For production:
    // Use a simpler setup without DevTools for better performance
    return compose(middlewares, dynostore(dynamicReducers()));
  };

  const state = {
    ...preloadedState,
  };

  const store = createStore(rootReducer, state, getEnhancers());

  return { store };
}

export const sessionToken = "sessionToken";
export const sessionRole = "sessionRole";
export const sessionStudies = "sessionStudies";
export const sessionClaims = "sessionClaims";
export const sessionSites = "sessionSites";
export const sessionUsername = "sessionUsername";
export const sessionName = "sessionName";
export const sessionLanguage = "sessionLanguage";

export const publicLanguage = "publicLanguage";

const splitHostname = () => window.location.hostname.split(".");

/**
 * Retrieves the current host by joining the last two parts of the split hostname.
 * @returns {string} The current host as a string.
 * @example
 * // If window.location.hostname is "app.example.com"
 * const host = currentHost();
 * console.log(host); // Outputs: "example.com"
 */
const runtimeHost = () => String(splitHostname().slice(-2).join("."));

let cachedApiUrl = null;
let cachedInfoBaseUrl = null;
let cachedBackendVersion = null;
let cachedNutritionixAcceptedLocales = null;

export const appStudy = "ux007-cl302";

export const backendVersion = async () => {
  if (cachedBackendVersion === null) {
    try {
      const baseUrl = await apiUrl();
      const fullUrl = `${baseUrl}/api/version`;
      console.log("Attempting to fetch backend version from:", fullUrl);
      const response = await axiosInstance.get(fullUrl, {
        timeout: 3000,
      });
      cachedBackendVersion = response.data?.version || "unable to retrieve";
    } catch (error) {
      console.error("Error fetching backend version:", error);
      cachedBackendVersion = "unable to fetch";
    }
    console.log("  backendVersion", cachedBackendVersion);
  }
  return cachedBackendVersion;
};

export const apiUrl = async () => {
  if (cachedApiUrl === null) {
    await fetchConfig();
  }
  return cachedApiUrl;
};

export const infoBaseUrl = async () => {
  if (cachedInfoBaseUrl === null) {
    await fetchConfig();
  }
  return cachedInfoBaseUrl;
};

export const nutritionixAcceptedLocales = async () => {
  if (cachedNutritionixAcceptedLocales === null) {
    await fetchConfig();
  }
  // Convert the string to an array
  return (
    cachedNutritionixAcceptedLocales
      ?.split(",")
      .map((locale) => locale.trim()) || []
  );
};

let cachedConfigResponse = null;
let configFetchUnavailable = false;

const getConfigResponse = async () => {
  if (cachedConfigResponse === null && !configFetchUnavailable) {
    try {
      cachedConfigResponse = await axiosInstance.get("/config/", {
        timeout: 5000, // Increased timeout to 5 seconds
        validateStatus: () => true, // This allows any status code to resolve
      });
    } catch (error) {
      configFetchUnavailable = true;
      throw new Error("Error fetching config: " + JSON.stringify(error));
    }
  }
  if (configFetchUnavailable) {
    throw new Error("Application configuration unavailable");
  }
  return cachedConfigResponse;
};

export const fetchConfig = async () => {
  // Helper function to get value from environment or config response
  const getValue = (envVar, headerKey, response) => {
    if (process?.env?.[envVar]) {
      console.log(`  ${envVar} from environment:`, process.env[envVar]);
      return process.env[envVar];
    }
    if (response?.headers?.[headerKey]) {
      console.log(`  ${headerKey} from config:`, response.headers[headerKey]);
      return response.headers[headerKey];
    }
    return null;
  };

  const configResponse = await getConfigResponse();

  // Handle apiUrl
  cachedApiUrl = getValue(
    "REACT_APP_BACKEND_API",
    "x-backend-api",
    configResponse
  );
  if (!cachedApiUrl) {
    const stage = configResponse?.headers?.["x-stage"];
    if (stage) {
      cachedApiUrl = `https://${appStudy}.v1.api.${stage}.${runtimeHost()}`;
      console.log("  apiUrl (based on stage):", cachedApiUrl);
    } else {
      cachedApiUrl = `https://${appStudy}.v1.api.ci.${runtimeHost()}`;
      console.log("  apiUrl (default):", cachedApiUrl);
    }
  }

  // Handle infoBaseUrl
  cachedInfoBaseUrl = getValue(
    "REACT_APP_INFO_BASE_URL",
    "x-info-base-url",
    configResponse
  );
  if (!cachedInfoBaseUrl) {
    cachedInfoBaseUrl =
      "https://rarify-public-assets-ci.s3.us-east-2.amazonaws.com/info/";
    console.log("  infoBaseUrl (default):", cachedInfoBaseUrl);
  }

  // Handle nutritionixAcceptedLocales
  cachedNutritionixAcceptedLocales = getValue(
    "REACT_APP_NUTRITIONIX_ACCEPTED_LOCALES",
    "x-nutritionix-accepted-locales",
    configResponse
  );
  if (!cachedNutritionixAcceptedLocales) {
    cachedNutritionixAcceptedLocales =
      "en_US, de_DE, fr_FR, es_ES, en_GB, it_IT, nl_NL, es_MX, pt_PT, ja_JP, ko_KR, pt_BR, ar_SA, tr_TR, pl_PL, cs_CZ";
    console.log(
      "  nutritionixAcceptedLocales (default):",
      cachedNutritionixAcceptedLocales
    );
  }
};
